"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, "GenericFieldHandler", {
    enumerable: true,
    get: function() {
        return GenericFieldHandler;
    }
});
const _handlerUtils = require("~/db/field-handler/utils/handlerUtils");
const _dbHelpers = require("~/helpers/dbHelpers");
const _sqlSanitize = require("~/helpers/sqlSanitize");
let GenericFieldHandler = class GenericFieldHandler {
    async select(qb, column, options) {
        const { alias, context, baseModel, tnPath } = options;
        const columnName = await (0, _dbHelpers.getColumnName)(context, column, await baseModel.model.getColumns(context));
        const tableName = alias || tnPath || '';
        const fullColumnName = tableName ? `${tableName}.${columnName}` : columnName;
        const selectAlias = (0, _sqlSanitize.sanitize)((0, _dbHelpers.getAs)(column) || columnName);
        const selectColumn = (0, _sqlSanitize.sanitize)(fullColumnName);
        qb.select({
            [selectAlias]: selectColumn
        });
    }
    async filter(knex, filter, column, options) {
        var _options_customWhereClause;
        const { alias } = options;
        const val = filter.value;
        const field = (_options_customWhereClause = options.customWhereClause) != null ? _options_customWhereClause : alias ? `${alias}.${column.column_name}` : column.column_name;
        return await this.handleFilter({
            val,
            sourceField: field
        }, {
            knex,
            filter,
            column
        }, options);
    }
    async handleFilter(args, rootArgs, options) {
        const { sourceField, val } = args;
        const { filter } = rootArgs;
        let filterOperation;
        switch(filter.comparison_op){
            case 'eq':
                filterOperation = this.filterEq;
                break;
            case 'neq':
                filterOperation = this.filterNeq;
                break;
            case 'not':
                filterOperation = this.filterNot;
                break;
            case 'like':
                filterOperation = this.filterLike;
                break;
            case 'nlike':
                filterOperation = this.filterNlike;
                break;
            case 'empty':
            case 'null':
            case 'blank':
                filterOperation = this.filterBlank;
                break;
            case 'notempty':
            case 'notnull':
            case 'notblank':
                filterOperation = this.filterNotblank;
                break;
            case 'is':
                filterOperation = this.filterIs;
                break;
            case 'isnot':
                filterOperation = this.filterIsnot;
                break;
            case 'gt':
                filterOperation = this.filterGt;
                break;
            case 'ge':
            case 'gte':
                filterOperation = this.filterGte;
                break;
            case 'lt':
                filterOperation = this.filterLt;
                break;
            case 'le':
            case 'lte':
                filterOperation = this.filterLte;
                break;
            case 'in':
                filterOperation = this.filterIn;
                break;
            case 'allof':
                filterOperation = this.filterAllof;
                break;
            case 'nallof':
                filterOperation = this.filterNallof;
                break;
            case 'anyof':
                filterOperation = this.filterAnyof;
                break;
            case 'nanyof':
                filterOperation = this.filterNanyof;
                break;
            default:
                filterOperation = _handlerUtils.unsupportedFilter;
        }
        // to keep `this` reference
        return await filterOperation.bind(this)({
            val,
            sourceField
        }, rootArgs, options);
    }
    // region filter comparisons
    async filterEq(args, rootArgs, _options) {
        const { val, sourceField } = args;
        const { knex } = rootArgs;
        return {
            rootApply: undefined,
            clause: (qb)=>{
                if (!(0, _handlerUtils.ncIsStringHasValue)(val)) {
                    qb.where((nestedQb)=>{
                        nestedQb.whereNull(sourceField);
                    });
                } else {
                    qb.where(knex.raw('?? = ?', [
                        sourceField,
                        val
                    ]));
                }
            }
        };
    }
    async filterNeq(args, rootArgs, _options) {
        const { val, sourceField } = args;
        const { knex } = rootArgs;
        return {
            rootApply: undefined,
            clause: (qb)=>{
                if (!(0, _handlerUtils.ncIsStringHasValue)(val)) {
                    qb.where((nestedQb)=>{
                        nestedQb.where(knex.raw("?? != ''", [
                            sourceField
                        ])).whereNotNull(sourceField);
                    });
                } else {
                    qb.where((nestedQb)=>{
                        nestedQb.where(knex.raw('?? != ?', [
                            sourceField,
                            val
                        ])).orWhereNull(sourceField);
                    });
                }
            }
        };
    }
    async filterNot(args, rootArgs, options) {
        return this.filterNeq(args, rootArgs, options);
    }
    async filterLike(args, rootArgs, _options) {
        const { val, sourceField } = args;
        const { knex } = rootArgs;
        return {
            rootApply: undefined,
            clause: (qb)=>{
                if (!(0, _handlerUtils.ncIsStringHasValue)(val)) {
                    qb.where((subQb)=>{
                        subQb.where(sourceField, '');
                        subQb.whereNull(sourceField);
                    });
                } else {
                    qb.where(knex.raw('?? like ?', [
                        sourceField,
                        `%${val}%`
                    ]));
                }
            }
        };
    }
    async filterNlike(args, rootArgs, _options) {
        const { sourceField } = args;
        let { val } = args;
        const { knex } = rootArgs;
        return {
            rootApply: undefined,
            clause: (qb)=>{
                if (!(0, _handlerUtils.ncIsStringHasValue)(val)) {
                    // val is empty -> all values including NULL but empty strings
                    qb.whereNot(sourceField, '');
                    qb.orWhereNull(sourceField);
                } else {
                    val = val.startsWith('%') || val.endsWith('%') ? val : `%${val}%`;
                    qb.whereNot(knex.raw(`?? like ?`, [
                        sourceField,
                        val
                    ]));
                    if (val !== '%%') {
                        // if value is not empty, empty or null should be included
                        qb.orWhere(sourceField, '');
                        qb.orWhereNull(sourceField);
                    } else {
                        // if value is empty, then only null is included
                        qb.orWhereNull(sourceField);
                    }
                }
            }
        };
    }
    async filterBlank(args, rootArgs, _options) {
        const { sourceField } = args;
        const { knex } = rootArgs;
        return {
            rootApply: undefined,
            clause: (qb)=>{
                qb.where((nestedQb)=>{
                    nestedQb.whereNull(sourceField).orWhere(knex.raw("?? = ''", [
                        sourceField
                    ]));
                });
            }
        };
    }
    async filterNotblank(args, rootArgs, _options) {
        const { sourceField } = args;
        const { knex } = rootArgs;
        return {
            rootApply: undefined,
            clause: (qb)=>{
                qb.where((nestedQb)=>{
                    nestedQb.whereNotNull(sourceField).orWhere(knex.raw("?? != ''", [
                        sourceField
                    ]));
                });
            }
        };
    }
    async filterIs(args, rootArgs, options) {
        const { val } = args;
        switch(val){
            case 'blank':
            case 'empty':
                {
                    return this.filterBlank(args, rootArgs, options);
                }
            case 'notblank':
            case 'notempty':
                {
                    return this.filterNotblank(args, rootArgs, options);
                }
        }
    }
    async filterIsnot(args, rootArgs, options) {
        const { val } = args;
        switch(val){
            case 'blank':
            case 'empty':
                {
                    return this.filterNotblank(args, rootArgs, options);
                }
            case 'notblank':
            case 'notempty':
                {
                    return this.filterBlank(args, rootArgs, options);
                }
        }
    }
    async filterGt(args, _rootArgs, _options) {
        const { val, sourceField } = args;
        return {
            rootApply: undefined,
            clause: (qb)=>{
                qb.where(sourceField, '>', val);
            }
        };
    }
    async filterGte(args, _rootArgs, _options) {
        const { val, sourceField } = args;
        return {
            rootApply: undefined,
            clause: (qb)=>{
                qb.where(sourceField, '>=', val);
            }
        };
    }
    async filterLt(args, _rootArgs, _options) {
        const { val, sourceField } = args;
        return {
            rootApply: undefined,
            clause: (qb)=>{
                qb.where(sourceField, '<', val);
            }
        };
    }
    async filterLte(args, _rootArgs, _options) {
        const { val, sourceField } = args;
        return {
            rootApply: undefined,
            clause: (qb)=>{
                qb.where(sourceField, '<=', val);
            }
        };
    }
    async innerFilterAllAnyOf(args, rootArgs, _options) {
        const { val, sourceField } = args;
        const { filter, knex } = rootArgs;
        // Condition for filter, without negation
        const condition = (builder)=>{
            const items = val == null ? void 0 : val.split(',');
            for(let i = 0; i < (items == null ? void 0 : items.length); i++){
                const bindings = [
                    sourceField,
                    `%,${items[i]},%`,
                    sourceField,
                    `%, ${items[i]},%`
                ];
                const sql = "(CONCAT(',', ??, ',') like ? OR CONCAT(',', ??, ',') like ?)";
                if (i === 0) {
                    builder = builder.where(knex.raw(sql, bindings));
                } else {
                    if (filter.comparison_op === 'allof' || filter.comparison_op === 'nallof') {
                        builder = builder.andWhere(knex.raw(sql, bindings));
                    } else {
                        builder = builder.orWhere(knex.raw(sql, bindings));
                    }
                }
            }
        };
        return {
            rootApply: undefined,
            clause: (qb)=>{
                qb.where((subQb)=>{
                    if (filter.comparison_op === 'allof' || filter.comparison_op === 'anyof') {
                        subQb.where(condition);
                    } else {
                        subQb.whereNot(condition).orWhereNull(sourceField);
                    }
                });
            }
        };
    }
    async filterAllof(args, rootArgs, options) {
        return this.innerFilterAllAnyOf(args, rootArgs, options);
    }
    async filterNallof(args, rootArgs, options) {
        return this.innerFilterAllAnyOf(args, rootArgs, options);
    }
    async filterAnyof(args, rootArgs, options) {
        return this.innerFilterAllAnyOf(args, rootArgs, options);
    }
    async filterNanyof(args, rootArgs, options) {
        return this.innerFilterAllAnyOf(args, rootArgs, options);
    }
    async filterIn(args, _rootArgs, _options) {
        const { val, sourceField } = args;
        return {
            rootApply: undefined,
            clause: (qb)=>{
                var _val_split;
                qb.whereIn(sourceField, Array.isArray(val) ? val : val == null ? void 0 : (_val_split = val.split) == null ? void 0 : _val_split.call(val, ','));
            }
        };
    }
    // endregion filter comparisons
    async verifyFilter(_filter, _column) {
        return {
            isValid: true
        };
    }
    async parseUserInput(params) {
        return {
            value: params.value
        };
    }
    async parseDbValue(params) {
        return {
            value: params.value
        };
    }
    constructor(){
        // to be implemented on checkbox itself
        this.filterChecked = _handlerUtils.unsupportedFilter;
        // to be implemented on checkbox itself
        this.filterNotchecked = _handlerUtils.unsupportedFilter;
    }
};
